/**
 * Format Utility Functions
 * Provides helper functions for formatting data (e.g., bytes, dates).
 * 
 * Main logic:
 * - Format bytes to human-readable string.
 * - Estimate video size based on resolution and duration.
 * - Add more formatting utilities as needed.
 * 
 * All variable names, logs, and comments are in English.
 */

/**
 * Estimate video size based on resolution and duration
 * Uses industry average bitrates for different resolutions
 * @param resolution - Video resolution (e.g., "1280x720")
 * @param duration - Video duration in seconds
 * @returns Estimated size in bytes
 */
export function estimateVideoSize(resolution: string, duration: number): number {
  if (!resolution || !duration) {
    return 0;
  }
  
  // Extract height from resolution (e.g., "1280x720" -> 720)
  const height = parseInt(resolution.split('x')[1]);
  if (!height) {
    return 0;
  }
  
  // Estimate bitrate (in kbps) based on height
  // These are rough estimates based on industry averages
  let bitrate = 0;
  
  if (height >= 1080) {
    bitrate = 4500; // 1080p ~ 4-5 Mbps
  } else if (height >= 720) {
    bitrate = 2500; // 720p ~ 2-3 Mbps
  } else if (height >= 480) {
    bitrate = 1500; // 480p ~ 1-2 Mbps
  } else if (height >= 360) {
    bitrate = 750;  // 360p ~ 0.5-1 Mbps
  } else if (height >= 240) {
    bitrate = 500;  // 240p ~ 0.3-0.7 Mbps
  } else {
    bitrate = 300;  // Lower resolutions
  }
  
  // Calculate bytes: (bitrate in kbps * duration in seconds) / 8 bits per byte * 1024 bytes per KB
  const bytes = Math.round((bitrate * duration) / 8 * 1024);
  
  return bytes;
}

/**
 * Format bytes to human-readable string (e.g., 1.2 MB)
 * If bytes is 0, returns an estimated size message
 * @param bytes - Size in bytes
 * @param resolution - Optional video resolution for estimation
 * @param duration - Optional video duration for estimation
 * @returns Formatted string
 */
export function formatBytes(bytes: number, resolution?: string, duration?: number): string {
  // If size is 0 but we have resolution and duration, try to estimate
  if (bytes === 0 && resolution && duration) {
    const estimatedBytes = estimateVideoSize(resolution, duration);
    
    if (estimatedBytes > 0) {
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(estimatedBytes) / Math.log(k));
      const formattedSize = "~" + parseFloat((estimatedBytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i] + " (estimated)";
      return formattedSize;
    }
    return "Size unknown (streaming)";
  }
  
  // Regular formatting for non-zero sizes
  if (bytes === 0) {
    return "Size unknown";
  }
  
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Format duration from milliseconds to human-readable string
 * @param ms - Duration in milliseconds
 * @returns Formatted string (e.g., "2h 30m", "45s")
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  return `${seconds}s`;
}

/**
 * Format number with thousand separators
 * @param num - Number to format
 * @returns Formatted string (e.g., "1,234,567")
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Format percentage
 * @param value - Value to format as percentage
 * @param total - Total value (optional, defaults to 100)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, total: number = 100): string {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return `${Math.round(percentage)}%`;
}
